import { fabric } from 'fabric';
import EventPublisher from '../event_publisher';
import GroupSelection from './group_selection';
import CanvasSizeCalculator from './canvas_size_calculator';
import Alignment from './alignment';
import Distribution from './distribution';

export default class Renderer {
  // eslint-disable-next-line max-params
  constructor(canvasElement, positions, landmarks, rentalPeriods, readOnly = false, canvasSize = {}) {
    this.canvasElement = canvasElement;
    this.positions = positions;
    this.landmarks = landmarks;
    this.rentalPeriods = rentalPeriods;
    this.isDirty = false;
    this.readOnly = readOnly;
    this.canvasSizeCalculator = CanvasSizeCalculator.create(positions, landmarks, canvasSize);
  }

  render() {
    this.setupLayoutCanvas();
    this.renderPositions();
    this.renderLandmarks();
    this.setupEventListeners();
  }

  setupLayoutCanvas() {
    const { width, height } = this.canvasSizeCalculator.calculate();
    this.canvas = new fabric.Canvas(this.canvasElement, {
      width,
      height,
      backgroundColor: '#fff',
    });

    this.setupReadOnlyCanvas();
    this.canvasContainer = document.querySelector('.canvas-container');
    this.canvasContainer.style.minWidth = `${width}px`;
  }

  setupEventListeners() {
    this.canvas.on('selection:cleared', this.onSelectionCleared);
    this.canvas.on('object:modified', this.onStateModified);
    this.canvas.on('selection:created', this.onSelectionCreated);
    this.canvas.on('selection:updated', this.onSelectionUpdated);
    window.addEventListener('object-moved', this.onObjectMoved);
    window.addEventListener('object-moving', this.onObjectMoving);
    window.addEventListener('layout-saved', this.onLayoutSaved);
    window.addEventListener('beforeunload', this.onBeforeUnload, { capture: true });
  }

  setupReadOnlyCanvas() {
    if (this.readOnly) {
      this.canvas.hoverCursor = 'pointer';
      this.canvas.selection = false;
    }
  }

  onStateModified = () => {
    this.isDirty = true;
  }

  onLayoutSaved = () => {
    this.isDirty = false;
  }

  onBeforeUnload = event => {
    if (this.isDirty) {
      event.preventDefault();
      return event.returnValue = 'Are you sure you want to exit?'; // eslint-disable-line no-return-assign
    }
  }

  onSelectionCleared() {
    EventPublisher.publish('selection-cleared');
  }

  onSelectionCreated = () => {
    this.handleMultipleSelection();
  }

  onSelectionUpdated = () => {
    this.handleMultipleSelection();
  }

  onObjectMoving = e => {
    const { object } = e.detail;
    const { width, height } = this.canvasSizeCalculator.calculateWithUpdatedObject(object);
    this.setCanvasSize(width, height);
  }

  onObjectMoved = e => {
    const { object } = e.detail;
    const { width, height } = this.canvasSizeCalculator.updateObject(object).calculate();
    this.setCanvasSize(width, height);
  }

  setCanvasSize(width, height) {
    this.canvas.setHeight(height);
    this.canvas.setWidth(width);
    this.canvasContainer.style.minWidth = `${width}px`;
  }

  handleMultipleSelection() {
    this.selection?.tearDownListeners();
    if (this.canvas.getActiveObjects().length > 1) {
      this.selection = GroupSelection.fromCanvas(this.canvas);
      this.selection.turnOffControls().setupListeners();
    }
  }

  renderLandmarks() {
    for (let landmark of this.landmarks) {
      landmark.renderOn(this.canvas, this.readOnly);
    }
  }

  renderLandmark(landmark) {
    landmark.renderOn(this.canvas, this.readOnly);
  }

  renderPositions() {
    for (let position of this.positions) {
      this.checkBookedPosition(position)
      position.renderOn(this.canvas, this.readOnly);
    }
  }

  renderPosition(position) {
    position.renderOn(this.canvas, this.readOnly);
    position.setActive();
  }

  checkBookedPosition(position) {
    if (!this.readOnly) return;

    if (this.rentalPeriods.some(rp => rp.inventoryUnitId === position.inventoryUnitId)) {
      position.setBookedPosition(true)
    } else {
      position.setBookedPosition(false)
    }
  }

  align(direction) {
    new Alignment(this.canvas).align(direction);
  }

  distribute(direction) {
    new Distribution(this.canvas).distribute(direction);
  }
}
